import { useState, useEffect } from 'react';
import { isPlatform, getPlatforms } from '@ionic/react';

export interface PlatformDetection {
  isMobile: boolean;
  isTablet: boolean;
  isAppMobile: boolean;
  isSmartTV: boolean;
  isDesktop: boolean;
  platforms: string;
}

// Detección robusta de tablet que evita falsos positivos
export const isTablet = (() => {
  const ionicIsTablet = isPlatform('tablet');
  const userAgent = navigator.userAgent.toLowerCase();

  // Si Ionic dice que es tablet, aplicar validaciones adicionales
  if (ionicIsTablet) {
    // Lista de sistemas operativos desktop que nunca deberían ser tablets
    const desktopOS = [/mac os x/, /windows nt/, /linux/, /ubuntu/, /debian/, /fedora/, /centos/];

    const isDesktopOS = desktopOS.some((pattern) => pattern.test(userAgent));

    // Si tiene OS desktop y resolución alta, no es tablet
    if (isDesktopOS && window.screen.width >= 1024) {
      return false;
    }

    // Verificar si es específicamente iPad (que sí es tablet)
    const isIpad =
      /ipad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIpad) {
      return true;
    }
  }

  // Para casos no ambiguos, usar la detección de Ionic
  return ionicIsTablet;
})();

const isMobileWeb = isPlatform('mobileweb');

// Mobile app detection using Ionic's isPlatform
export const isAppMobile = (() => {
  const mobile = isPlatform('mobile');
  const mobileWeb = isPlatform('mobileweb');
  const isHybrid = isPlatform('hybrid');
  const isCapacitor = isPlatform('capacitor');

  // Log de debug para diagnosticar
  console.log('🔍 Platform Detection Debug:', {
    mobile,
    mobileWeb,
    isHybrid,
    isCapacitor,
    hasCapacitorObject: !!(window as any).Capacitor,
    platforms: getPlatforms().join(', '),
  });

  // Detectar si estamos en una app nativa (Capacitor)
  const isNativeApp = isCapacitor || isHybrid || !!(window as any).Capacitor;

  const result = mobile && !mobileWeb;

  // Si detectamos que es app nativa, siempre retornar true
  if (isNativeApp) {
    console.log('📱 Native mobile app detected via Capacitor');
    return true;
  }

  // Force web mode if dev tools are open and we're in browser
  const isInBrowser = !(window as any).Capacitor;
  if (result && isInBrowser) {
    console.log('🔧 Forcing web mode - detected dev tools mobile simulation');
    // return false;
  }

  console.log('📊 Final isAppMobile result:', result);
  return result;
})();

// Web mobile detection (existing logic for responsive web)
export const isMobile =
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
  !isTablet;

/* eslint-disable max-len */
export const isSmartTV =
  /smart-tv|smarttv|smart tv|googletv|appletv|viera|internet.tv|netcast|nettv|opera tv|hbbtv|pov_tv|kdl|bravia|dlnadoc|roku|ce-html/i.test(
    navigator.userAgent
  );

const detectIsScanner = async () => {
  const ua: any = (navigator as any).userAgentData;
  if (!ua?.getHighEntropyValues) return false;

  const { model, platform } = await ua.getHighEntropyValues(['model', 'platform']);

  // Zebra: TCxx, ECxx, MCxx
  const isZebra = /^(TC|MC|EC)\d+/i.test(model ?? '');

  // Keyence serie BT (ej: BT-A60OM)
  const isKeyence = /^BT-A\d+/i.test(model ?? '');

  return platform.toLowerCase() === 'android' && (isZebra || isKeyence);
};

// Helper function for platform detection
const detectPlatform = (): PlatformDetection => {
  const viewportWidth = window.innerWidth;
  try {
    return {
      isMobile,
      isTablet,
      isAppMobile,
      isSmartTV,
      isDesktop: !isMobile && !isTablet && !isSmartTV,
      platforms: getPlatforms().join(),
    };
  } catch (error) {
    console.warn('Error detecting platform:', error);

    // Fallback detection
    return {
      isMobile: viewportWidth <= 768,
      isTablet: viewportWidth > 768 && viewportWidth <= 1024,
      isAppMobile: false,
      isSmartTV: false,
      isDesktop: viewportWidth > 1024,
      platforms: '',
    };
  }
};

/**
 * Hook for robust platform detection using Ionic's platform detection
 * combined with responsive design patterns
 */
export const usePlatformDetection = (): PlatformDetection => {
  const [platformData, setPlatformData] = useState<PlatformDetection>(() => {
    // Initial detection
    return detectPlatform();
  });

  useEffect(() => {
    const handleResize = () => {
      setPlatformData(detectPlatform());
    };

    // Listen for viewport changes
    window.addEventListener('resize', handleResize);

    // Listen for device orientation changes
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return platformData;
};

// Export individual detection functions for backwards compatibility
export const getIsTablet = () => detectPlatform().isTablet;
export const getIsMobile = () => detectPlatform().isMobile;
export const getIsAppMobile = () => detectPlatform().isAppMobile;
export const getIsSmartTV = () => detectPlatform().isSmartTV;
export const getIsDesktop = () => detectPlatform().isDesktop;
export const getIsScanner = () => detectIsScanner();

/**
 * Get OS version from User Agent (works on all platforms)
 * Returns the OS version string or undefined if not detectable
 */
export const getOSVersion = (): string | undefined => {
  const ua = navigator.userAgent;

  // iOS detection
  const iosMatch = ua.match(/OS (\d+[._]\d+[._]?\d*)/);
  if (iosMatch) {
    return iosMatch[1].replace(/_/g, '.');
  }

  // Android detection
  const androidMatch = ua.match(/Android (\d+\.?\d*\.?\d*)/);
  if (androidMatch) {
    return androidMatch[1];
  }

  // macOS detection
  const macMatch = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
  if (macMatch) {
    return macMatch[1].replace(/_/g, '.');
  }

  // Windows detection
  const winMatch = ua.match(/Windows NT (\d+\.\d+)/);
  if (winMatch) {
    const winVersionMap: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    };
    return winVersionMap[winMatch[1]] || winMatch[1];
  }

  return undefined;
};

/**
 * Get device model from User Agent (works on all platforms)
 * Returns the device model string or undefined if not detectable
 */
export const getDeviceModel = (): string | undefined => {
  const ua = navigator.userAgent;

  // iPhone detection
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  }

  // iPad detection
  if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'iPad';
  }

  // Android device model
  const androidMatch = ua.match(/;\s*([^;)]+)\s*Build\//);
  if (androidMatch) {
    return androidMatch[1].trim();
  }

  // Generic mobile
  if (/Mobile/.test(ua)) {
    return 'Mobile Device';
  }

  // Desktop
  if (/Macintosh/.test(ua)) {
    return 'Mac';
  }
  if (/Windows/.test(ua)) {
    return 'Windows PC';
  }

  return undefined;
};

/**
 * Get tracking platform string for analytics
 * Returns platform identifier for tracking events
 */
export const getTrackingPlatform = (): string => {
  const platforms = getPlatforms();

  if (isAppMobile) {
    if (platforms.includes('ios')) {
      return 'App iOS';
    }
    if (platforms.includes('android')) {
      return 'App Android';
    }
    return 'App Mobile';
  }

  if (isMobile) {
    return 'Web Mobile';
  }

  if (isTablet) {
    return 'Web Tablet';
  }

  return 'Web Desktop';
};
